import { useUserStore } from '@/store/user.store'
import { openNewWindow } from './browser'
import { notifyCrossTab } from './crossTabSync'
import { router } from '@/router'
import type { AnswerAnalysis, XFWord, XFWordsData } from '@/views/common/TopicPage/api/index.api'
import ClipboardJS from 'clipboard'
import { ElMessageBox, ElNotification } from 'element-plus'

import { useWordsColor } from '@/store/wordsColor.store'
import { CONSENT_KEY, REFUSE_KEY } from '@/utils/privacy-consent'

/**
 * 根据秒获取 00:00的时间格式
 * @param seconds --- 秒
 */

export function secondsToTime(seconds: number) {
  var minutes = Math.floor(seconds / 60)
  if (minutes < 0) {
    minutes = 0
  }
  var remainingSeconds = Math.floor(seconds % 60) // 四舍五入到整数秒
  if (remainingSeconds < 0) {
    remainingSeconds = 0
  }
  var formattedSeconds = remainingSeconds < 10 ? '0' + remainingSeconds : remainingSeconds
  var formattedMinutes = minutes < 10 ? '0' + minutes : minutes
  return formattedMinutes + ':' + formattedSeconds
}

/**
 * 获取时间距离当前时间间隔文本
 * @param dateTimeStamp 时间
 * @returns
 */
export function getDateDiff(dateTimeStamp: Date) {
  // 时间字符串转时间戳
  var timestamp = new Date(dateTimeStamp).getTime()

  var minute = 1000 * 60
  var hour = minute * 60
  var day = hour * 24
  var month = day * 30
  var year = day * 365
  var now = new Date().getTime()
  var diffValue = now - timestamp
  var result: any
  if (diffValue < 0) {
    return
  }
  var yearC = diffValue / year
  var monthC = diffValue / month
  var weekC = diffValue / (7 * day)
  var dayC = diffValue / day
  var hourC = diffValue / hour
  var minC = diffValue / minute
  if (yearC >= 1) {
    result = '' + parseInt(yearC.toString()) + '年前'
  } else if (monthC >= 1) {
    result = '' + parseInt(monthC.toString()) + '月前'
  } else if (weekC >= 1) {
    result = '' + parseInt(weekC.toString()) + '周前'
  } else if (dayC >= 1) {
    result = '' + parseInt(dayC.toString()) + '天前'
  } else if (hourC >= 1) {
    result = '' + parseInt(hourC.toString()) + '小时前'
  } else if (minC >= 1) {
    result = '' + parseInt(minC.toString()) + '分钟前'
  } else result = '刚刚'
  return result
}

/**
 * 单词文本JSON化 ${单词}
 * @param text 单词文本
 * @returns
 */
export function parseText(text: string) {
  if (!text) return []
  const regex = /(\$\{[^}]+\}|[A-Za-z0-9'\u2018\u2019]+|[^A-Za-z0-9'\u2018\u2019\s]+)/g
  const matches = text.match(regex)
  const result: {
    type: 'word' | 'symbol' | 'special_word'
    value: string
  }[] = []
  if (!matches) return result
  matches.forEach((item) => {
    if (item.startsWith('${') && item.endsWith('}')) {
      // If the item is a word in curly braces
      let cleanItem = item.replace('${', '').replace('}', '')
      result.push({ type: 'special_word', value: cleanItem })
    } else if (/^[A-Za-z0-9'\u2018\u2019]+$/.test(item)) {
      // If the item is a word
      result.push({ type: 'word', value: item })
    } else {
      // If the item is a symbol
      result.push({ type: 'symbol', value: item })
    }
  })
  return result
}

/**
 * 单词文本JSON化 ${单词}
 * @param text 单词文本
 * @returns
 */
export function parseConjunction(text: string) {
  if (!text) return []
  // 修改正则表达式，允许单词中包含连字符（-）
  const regex = /(\$\{[^}]+\}|[A-Za-z0-9'\u2018\u2019-]+|[^A-Za-z0-9'\u2018\u2019\s]+)/g
  const matches = text.match(regex)
  const result: {
    type: 'word' | 'symbol' | 'special_word'
    value: string
    status?: -1 | 0 | 1 | 2
  }[] = []
  if (!matches) return result
  matches.forEach((item) => {
    if (item.startsWith('${') && item.endsWith('}')) {
      const cleanItem = item.slice(2, -1) // 更高效的清理方式
      result.push({ type: 'special_word', value: cleanItem })
    } else if (/^[A-Za-z0-9'\u2018\u2019-]+$/.test(item)) {
      // 同步更新正则校验
      result.push({ type: 'word', value: item })
    } else {
      result.push({ type: 'symbol', value: item })
    }
  })
  return result
}

/**
 *适用于 听力题型....
 * @param text  文本json化
 * @returns TextToJson[]
 */
export const formatTextToJson = (text: string) => {
  const list: TextToJson[] = []
  const testJson = parseConjunction(text)
  testJson.map((item, index, arr) => {
    if (item.type === 'word') {
      list.push({
        text: item.value,
        type: item.type,
        texts: [item.value],
        answer: ''
      })
    }
    if (item.type === 'symbol') {
      list.push({
        text: item.value,
        type: item.type,
        texts: [item.value],
        answer: ''
      })
    }
    if (item.type === 'special_word') {
      list.push({
        text: item.value,
        type: item.type,
        texts: item.value.split('|'),
        answer: ''
      })
      list.push({
        text: ' ',
        type: 'blank',
        texts: [],
        answer: ''
      })
    }
    // 连接符不需要加空格处理
    if (arr[index].value == '—') return
    if (
      (arr[index + 1] && arr[index].type === 'word' && arr[index + 1].type !== 'symbol') ||
      (arr[index + 1] && arr[index].type === 'symbol' && arr[index + 1].type !== 'symbol')
    ) {
      list.push({
        text: ' ',
        type: 'blank',
        texts: [],
        answer: ''
      })
    }
  })
  return list
}
export interface TextToJson {
  text: string
  type: 'word' | 'symbol' | 'special_word' | 'blank'
  texts: string[]
  answer: string
}

/**
 * HTML 转义，防止内容被当作标签注入
 */
export const escapeHtml = (str: string) =>
  String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

/**
 * 生成练习记录评测
 * @param records API返回的答案分析数据
 * @param originalText 用户的原始输入（用于恢复包含撇号的单词）
 * @returns HTML string
 */
export const renderwriteRecords = (records: AnswerAnalysis[], originalText?: string) => {
  var recordStr = ''

  // 纯标点判断，用于去掉标点前多余空格
  const isPunct = (str: string) => str.length > 0 && !/[A-Za-z0-9]/.test(str)

  // 如果有原始输入，尝试从原始输入中恢复包含撇号的单词
  const originalWords = originalText ? originalText.split(/\s+/).filter(Boolean) : []
  let consumed = 0

  records.forEach(function (V, K) {
    let displayWord = V.key
    if (originalWords.length > 0) {
      // 从当前进度之后查找，避免 its/it's 这类歧义词恢复错位
      const matchIndex = originalWords.findIndex(
        (w, i) => i >= consumed && w.replace(/[''`]/g, '').toLowerCase() === V.key.toLowerCase()
      )
      if (matchIndex !== -1) {
        displayWord = originalWords[matchIndex]
        consumed = matchIndex + 1
      }
    }
    displayWord = escapeHtml(displayWord)

    let text = displayWord
    let style = ''
    if (V.value === 0) {
      style = 'color:#808080;text-decoration: line-through;'
    } else if (V.value === 1) {
      style = 'color:#6ead5c;'
    } else if (V.value === 2) {
      style = 'color:#e42104;font-weight: bold;'
      if (K === 0 || records[K - 1].value !== 2) {
        text = '(' + text
      }
      if (K === records.length - 1 || records[K + 1].value !== 2) {
        text = text + ')'
      }
    } else if (V.value === 3) {
      style = 'color:#6ead5c;text-decoration:underline;text-decoration-color: #FF9B50;'
    }

    if (isPunct(V.key)) {
      recordStr = recordStr.replace(/ $/, '')
    }
    recordStr += '<span style="' + style + '">' + text + '</span> '
  })
  return recordStr
}
/**
 *
 * @param count 将万以上的数据转成小数显示 10000=1w
 * @returns
 */
export const playCountFormat = (count?: number) => {
  if (!count) {
    return '0次'
  }
  if (count < 10000) {
    return count + '次'
  } else {
    return Math.round(count / 1000) / 10 + 'W次'
  }
}

/**
 * 生成口语分享结果页 URL（带内容审核）
 * @param audioId 音频id
 * @param qId 题目id
 * @param shareType 分享类型
 */
export const getShareResultUrl = (
  audioId: number | string,
  qId: number | string,
  shareType: number | string = 0
) => {
  const voiceId = BigInt(audioId).toString()
  return `${
    window.location.origin || 'https://www.ytaxx.com'
  }/appPage/share/result-censor/index.html?id=${voiceId}&questionId=${qId}&shareType=${shareType}`
}

/**
 * 生成 RA 分享结果页 URL（带内容审核）
 * @param audioId 音频id
 * @param qId 题目id
 * @param shareType 分享类型
 */
export const getShareRaResultUrl = (
  audioId: number | string,
  qId: number | string,
  shareType: number | string = 0
) => {
  const voiceId = BigInt(audioId).toString()
  return `${
    window.location.origin
  }/appPage/share/RA-censor/index.html?id=${voiceId}&questionId=${qId}&shareType=${shareType}`
}

/**
 *
 * @param audioId 音频id
 * @param qId 题目id
 */
export const shareResult = (
  audioId: number | string,
  qId: number,
  shareType: number = 0,
  type: number = 0
) => {
  const voiceId = BigInt(audioId).toString()
  http.post(
    '/api/voice/censor/submit',
    type === 1 ? { recordId: voiceId, type } : { voiceId, type }
  )
  const url = getShareResultUrl(audioId, qId, shareType)
  const clipboard = new ClipboardJS('#copy-result', {
    text: function () {
      return url
    }
  })
    .on('success', function () {
      ElNotification({
        message: '复制成功',
        type: 'success'
      })
      clipboard.destroy()
    })
    .on('error', function () {
      ElNotification({
        message: '复制失败,请手动复制',
        type: 'error'
      })
      ElMessageBox.alert(url, {
        title: '分享结果',
        confirmButtonText: 'OK'
      })
      clipboard.destroy()
    })
}

/**
 *
 * @param audioId 音频id
 * @param qId 题目id
 */
export const shareRaResult = (
  audioId: number | string,
  qId: number | string,
  shareType?: number | string,
  type: number = 0
) => {
  const voiceId = BigInt(audioId).toString()
  http.post(
    '/api/voice/censor/submit',
    type === 1 ? { recordId: voiceId, type } : { voiceId, type }
  )
  const url = getShareRaResultUrl(audioId, qId, shareType)
  const clipboard = new ClipboardJS('#copy-result', {
    text: function () {
      return url
    }
  })
    .on('success', function () {
      ElNotification({
        message: '复制成功',
        type: 'success'
      })
      clipboard.destroy()
    })
    .on('error', function () {
      ElNotification({
        message: '复制失败,请手动复制',
        type: 'error'
      })
      ElMessageBox.alert(url, {
        title: '分享结果',
        confirmButtonText: 'OK'
      })
      clipboard.destroy()
    })
}

export const percentage90 = (number?: number) => {
  if (!number) {
    return 0
  }
  return (number / 90) * 100
}
export function getQueryString(name: string) {
  const reg = new RegExp('(^|&)' + name + '=([^&]*)(&|$)', 'i')
  let r: RegExpMatchArray | null | string = decodeURI(window.location.search)
  r = r.substr(1).match(reg)
  if (r != null) return unescape(r[2])
  return null
}

/**
 * 当前页跳转到 VIP 页面（不打开新窗口）
 * 开通成功后由 VIP 页（pay-vip.vue）自行刷新用户信息
 */
export const toVip = (href: string) => {
  router.push(href)
}

export const dealSearchText = (text: string, index: number, key?: string) => {
  let originalText = text
  if (!key) {
    return originalText
  }
  let start = originalText.toUpperCase().indexOf(key.toUpperCase())
  if (index != -1) {
    originalText = originalText.substring(Math.max(0, start - index), originalText.length)
  }
  let tempText = text
  const hilightWord: string[] = []
  while (tempText.toUpperCase().indexOf(key.toUpperCase()) >= 0) {
    let start = tempText.toUpperCase().indexOf(key.toUpperCase())
    hilightWord.push(tempText.substring(start, start + key.length))
    tempText = tempText.substring(start + key.length, tempText.length)
  }
  hilightWord.forEach((word) => {
    originalText = originalText.replace(word, (originalText) => {
      return `<b>${originalText}</b>`
    })
  })
  return originalText
}

export const formatXfWords = (xfWords: string) => {
  const wordsColorStore = useWordsColor()
  var Regx = /^[A-Za-z0-9]/
  if (!xfWords) return []
  try {
    let result = JSON.parse(xfWords) as XFWordsData[]
    const newArr = result[0].words.map((word) => {
      // 分数颜色
      const result = wordsColorStore.wordsColor.find((item: any) => {
        return word.score < item.end && word.score >= item.start
      })
      let newWord: XFWord = word
      newWord.style = {}
      // 是单词
      newWord.style['color'] = result?.color
      if (newWord.text) {
        if (newWord.text.indexOf('[漏读]') !== -1) {
          newWord.text = '(' + newWord.text.replace('[漏读]', '') + ')'
        } else if (newWord.text.indexOf('[增读]') !== -1) {
          newWord.text = newWord.text.replace('[增读]', '')
          if (newWord.style) {
            newWord.style['text-decoration'] = 'line-through'
          }
        } else {
          if (Regx.test(newWord.text)) {
            newWord.type = 'word'
          } else {
            newWord.style.color = '#000'
          }
        }
      }
      return newWord
    })
    return newArr
  } catch {
    return []
  }
}
export const formatWords = (words: string) => {
  xfXmlParseToScore(words)

  if (!words) return null
  try {
    let result = JSON.parse(words) as XFWordsData
    return result
  } catch {
    return null
  }
}

export const scrollTitleView = () => {
  document.documentElement.scrollTo({
    left: 0,
    top: 131,
    behavior: 'smooth'
  })
}

import convert from 'xml-js'
import { http } from '.'

export interface WordScore {
  score: number
  syllables: {
    score: number
    syls: SyllableItem[]
  }[]
  audioUrl?: string
  /**
   * 音频开始时间
   */
  st?: number
  /**
   * 音频开始时间
   */
  end?: number
}

interface SyllableItem {
  /**0正常其余看文档 */
  type: 0 | 16 | 32 | 64 | 128
  text: string
}

/**
 * 音节对照表
 * @param key 音节字母
 * @returns
 */
export const getLetter = (key: string) => {
  const phonemeToLetterMap = new Map([
    ['aa', 'bː'],
    ['ɑː', 'f'],
    ['f', 'f'],
    ['ae', 'æ'],
    ['g', 'g'],
    ['ah', 'ʌ'],
    ['hh', 'h'],
    ['ao', 'ɔː'],
    ['jh', 'dʒ'],
    ['ar', 'eə'],
    ['k', 'k'],
    ['aw', 'aʊ'],
    ['l', 'l'],
    ['ax', 'ə'],
    ['m', 'm'],
    ['ay', 'aɪ'],
    ['n', 'n'],
    ['eh', 'e'],
    ['ng', 'ŋ'],
    ['er', 'ɜː'],
    ['p', 'p'],
    ['ey', 'eɪ'],
    ['r', 'r'],
    ['ih', 'ɪ'],
    ['s', 's'],
    ['ir', 'ɪə'],
    ['sh', 'ʃ'],
    ['iy', 'iː'],
    ['t', 't'],
    ['oo', 'ɒ'],
    ['th', 'θ'],
    ['v', 'v'],
    ['oy', 'ɒɪ'],
    ['w', 'w'],
    ['uh', 'ʊ'],
    ['y', 'j'],
    ['uw', 'uː'],
    ['z', 'z'],
    ['ur', 'ʊə'],
    ['zh', 'ʒ'],
    ['b', 'b'],
    ['dr', 'dr'],
    ['ch', 'tʃ'],
    ['dz', 'dz'],
    ['d', 'd'],
    ['tr', 'tr'],
    ['dh', 'ð'],
    ['ts', 'ts']
  ])
  return phonemeToLetterMap.get(key) || key
}

/**
 * 解析讯飞xml 获取单词评分分数和音节
 * @param xml 讯飞xml
 */
export function xfXmlParseToScore(xml: string) {
  const wordSyllables: WordScore[] = []
  const getWordSyll = (word: any) => {
    const payload: WordScore = {
      score: 0,
      syllables: []
    }
    if (!word) {
      return payload
    } else {
      if (word._attributes) {
        payload.score = Number(word?._attributes['total_score'] | 0) * 18 || 10
      }

      let syll = []
      if (Array.isArray(word.syll)) {
        syll.push(...word.syll)
      } else {
        syll.push(word.syll)
      }
      syll.forEach((item: any) => {
        const index = payload.syllables.push({
          score: item._attributes.syll_score,
          syls: []
        })

        if (item?.phone && Array.isArray(item.phone)) {
          item.phone.forEach((content: any) => {
            payload.syllables[index - 1].syls.push({
              text: getLetter(content._attributes.content || ''),
              type: content._attributes.dp_message
            })
          })
        } else {
          payload.syllables[index - 1].syls.push({
            text: getLetter(item?.phone?._attributes.content || ''),
            type: item?.phone?._attributes.dp_message
          })
        }
      })
      return payload
    }
  }
  try {
    const result1 = JSON.parse(convert.xml2json(xml, { compact: true, spaces: 4 }))
    const sentence = result1?.xml_result?.read_sentence?.rec_paper?.read_chapter?.sentence
    const xmlWord: any = (() => {
      if (Array.isArray(sentence)) {
        const words: any[] = []
        sentence.forEach((item) => {
          words.push(...item.word)
        })
        return words
      } else {
        return sentence.word
      }
    })()
    if (Array.isArray(xmlWord)) {
      xmlWord.forEach((item) => {
        wordSyllables.push(getWordSyll(item))
      })
    } else {
      wordSyllables.push(getWordSyll(xmlWord))
    }
    return wordSyllables
  } catch (error) {
    console.log(error)
    return []
  }
}

/**
 * !!!! 注意 模考专用解析
 * 解析讯飞模考xml 获取单词评分分数和音节
 * @param xml 讯飞xml
 */
export function xfXmlMockParseToScore(xml: string) {
  try {
    const payload: WordScore = {
      score: 0,
      syllables: []
    }
    const result1 = JSON.parse(convert.xml2json(xml, { compact: true, spaces: 4 }))

    const xmlWord = (() => {
      let result = result1?.xml_result?.read_sentence?.rec_paper?.read_chapter?.sentence?.word
      if (!result) {
        result = result1?.xml_result?.read_word?.rec_paper?.read_word?.sentence?.word
      }
      return result
    })()
    const word = (() => {
      if (Array.isArray(xmlWord)) {
        return xmlWord[0]
      } else {
        return xmlWord
      }
    })()

    if (!word) {
      throw new Error('')
    } else {
      if (word._attributes) {
        payload.score = Number(word?._attributes['total_score'] | 0) * 18 || 10
      }

      let syll = []
      if (Array.isArray(word.syll)) {
        syll.push(...word.syll)
      } else {
        syll.push(word.syll)
      }
      syll.forEach((item: any) => {
        const index = payload.syllables.push({
          score: item._attributes.syll_score,
          syls: []
        })

        if (item?.phone && Array.isArray(item.phone)) {
          item.phone.forEach((content: any) => {
            payload.syllables[index - 1].syls.push({
              text: getLetter(content._attributes.content || ''),
              type: content._attributes.dp_message
            })
          })
        } else {
          payload.syllables[index - 1].syls.push({
            text: getLetter(item?.phone?._attributes.content || ''),
            type: item?.phone?._attributes.dp_message
          })
        }
      })
      return payload
    }
  } catch (error) {
    console.error(error)
    return {
      score: 0,
      syllables: []
    } as WordScore
  }
}
/**
 *
 * @param val 输入内容
 */
export const formatInput = (val: string) => {
  // 先将弯引号统一转换为直引号，避免被过滤
  const normalized = val.replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'")
  return normalized.trimStart().replace(/[^\w;:'"|,.<>?~·!@#$%^&*()+\-—*/=\[\]{}/\\\s]/g, '')
}

export interface WordItem {
  word: string
  beg: number
  end: number
  score: number
  syllList: SyllList[]
}

interface SyllList {
  syll: string
  beg: number
  end: number
  score: number
  syllAccent: number
}
export const getWithmic = async () => {
  return await startRecording().then((res) => {
    return `${detectOS()}|${res?.label}`
  })
}

async function getCurrentRecordingDevice(stream: MediaStream) {
  try {
    const track = stream.getAudioTracks()[0]
    if (!track) {
      console.log('没有活动的音频轨道')
      return null
    }

    const devices = await navigator.mediaDevices.enumerateDevices()
    const currentDevice = devices.find(
      (device) => device.kind === 'audioinput' && device.deviceId === track.getSettings().deviceId
    )

    if (currentDevice) {
      console.log('当前录音设备:', currentDevice.label || '未知设备')
      return currentDevice
    } else {
      console.log('无法确定当前录音设备')
      return null
    }
  } catch (error) {
    console.error('获取当前设备失败:', error)
    return null
  }
}

// 使用示例
async function startRecording() {
  if (window.sessionStorage.getItem('Withmic')) {
    return Promise.resolve(
      JSON.parse(window.sessionStorage.getItem('Withmic') || JSON.stringify({ label: '' }))
    )
  }
  try {
    let stream: MediaStream | null = await navigator.mediaDevices.getUserMedia({ audio: true })
    const currentDevice = await getCurrentRecordingDevice(stream)

    stream.getTracks().forEach((track) => {
      track.stop() // 停止轨道
      track.enabled = false // 禁用轨道（可选）
    })
    stream = null
    window.sessionStorage.setItem('Withmic', JSON.stringify(currentDevice))
    // 使用stream进行录音...
    return currentDevice
  } catch {
    return Promise.resolve({ label: '' })
  }
}

function detectOS() {
  const userAgent = navigator.userAgent
  let os = '未知操作系统'

  if (userAgent.indexOf('Windows') !== -1) {
    os = 'Windows'
  } else if (userAgent.indexOf('Macintosh') !== -1 || userAgent.indexOf('Mac OS X') !== -1) {
    os = 'macOS'
  } else if (userAgent.indexOf('Linux') !== -1 && userAgent.indexOf('X11') !== -1) {
    os = 'Linux'
  } else if (userAgent.indexOf('Android') !== -1) {
    os = 'Android'
  } else if (
    userAgent.indexOf('iPhone') !== -1 ||
    userAgent.indexOf('iPad') !== -1 ||
    userAgent.indexOf('iPod') !== -1
  ) {
    os = 'iOS'
  } else if (userAgent.indexOf('CrOS') !== -1) {
    os = 'Chrome OS'
  }

  return os
}

/**
 * 携带token跳转网校课程
 * @returns
 */
export const toKT = () => {
  const userStore = useUserStore()
  const token = window.localStorage.getItem('token')
  var url =
    window.location.host === 'test-v2.ytaxx.com'
      ? 'https://test-ke.ytaxx.com'
      : 'https://kt.ytaxx.com'

  if (token && userStore.userInfo) {
    return openNewWindow(`${url}?mobileToken=${userStore.userInfo.mobileToken?.token}`)
  } else {
    return openNewWindow(url)
  }
}
/**
 * PTE分数转雅思分数
 * @param type 分数类型
 * @param score pte分数
 * @returns
 */
export const getCrosswalksScores = (
  type: 'Total' | 'Listening' | 'Reading' | 'Speaking' | 'Writing',
  score: number
) => {
  if (type === 'Total') {
    if (score >= 90) return '9.0'
    if (score >= 86) return '8.5'
    if (score >= 79) return '8.0'
    if (score >= 71) return '7.5'
    if (score >= 63) return '7.0'
    if (score >= 55) return '6.5'
    if (score >= 47) return '6.0'
    if (score >= 39) return '5.5'
    if (score >= 31) return '5.0'
    if (score >= 24) return '4.5'
  }
  if (type === 'Listening') {
    if (score >= 81) return '9.0'
    if (score >= 75) return '8.5'
    if (score >= 69) return '8.0'
    if (score >= 64) return '7.5'
    if (score >= 58) return '7.0'
    if (score >= 53) return '6.5'
    if (score >= 47) return '6.0'
    if (score >= 40) return '5.5'
    if (score >= 33) return '5.0'
    if (score >= 26) return '4.5'
  }
  if (type === 'Reading') {
    if (score >= 81) return '9.0'
    if (score >= 75) return '8.5'
    if (score >= 70) return '8.0'
    if (score >= 65) return '7.5'
    if (score >= 59) return '7.0'
    if (score >= 54) return '6.5'
    if (score >= 48) return '6.0'
    if (score >= 42) return '5.5'
    if (score >= 36) return '5.0'
    if (score >= 29) return '4.5'
  }
  if (type === 'Speaking') {
    if (score >= 90) return '8.5'
    if (score >= 88) return '8.0'
    if (score >= 83) return '7.5'
    if (score >= 76) return '7.0'
    if (score >= 66) return '6.5'
    if (score >= 54) return '6.0'
    if (score >= 39) return '5.5'
    if (score >= 24) return '5.0'
    if (score >= 12) return '4.5'
  }
  if (type === 'Writing') {
    if (score >= 90) return '8.5'
    if (score >= 85) return '8.0'
    if (score >= 77) return '7.5'
    if (score >= 69) return '7.0'
    if (score >= 60) return '6.5'
    if (score >= 51) return '6.0'
    if (score >= 41) return '5.5'
    if (score >= 29) return '5.0'
    if (score >= 17 && score <= 28) return '4.5'
  }
  return '-'
}

/**
 * 清空localStorage，只保留指定的key
 * @param {string|string[]} keysToKeep - 要保留的key，可以是字符串或字符串数组
 * @returns {void}
 */
const KEYS: string[] = [
  'isOpenTarget',
  '_arms_uid',
  '_arms_session',
  'home_toolbar_display_mode',
  // 隐私授权状态：退出登录/注销清空存储时保留，避免用户被重复询问授权
  CONSENT_KEY,
  REFUSE_KEY
]
export function clearLocalStorageKeeping(keysToKeep = KEYS) {
  // 将参数统一转换为数组
  const keysToPreserve = Array.isArray(keysToKeep) ? keysToKeep : [keysToKeep]

  // 获取localStorage中所有的key
  const allKeys = Object.keys(localStorage)

  // 过滤出需要删除的key
  const keysToRemove = allKeys.filter((key) => !keysToPreserve.includes(key))

  // 删除不需要保留的key
  keysToRemove.forEach((key) => {
    localStorage.removeItem(key)
  })

  console.log(`已清空localStorage，保留了: ${keysToPreserve.join(', ')}`)
  console.log(`删除了 ${keysToRemove.length} 个键值对`)

  // 退出登录 / 注销 / 登录失效：通知同域名下其他窗口刷新，同步登录态
  notifyCrossTab('logout')
}

/**
 * SWT 词数 — 与后端 SwtCupcakeStrategy.countWordsForSwtForm 一致
 * @param content
 * @returns
 */
export function countWordsForSwtForm(content: string | null | undefined): number {
  if (content == null || String(content).trim() === '') {
    return 0
  }
  let normalized = String(content)
    .replace(/"/g, '') // 仅英文双引号
    .replace(/\./g, ' ') // 每个 . 变空格
  normalized = normalized.replace(/ +/g, ' ').trim() // Java: replaceAll(" +", " ")
  if (normalized === '') {
    return 0
  }
  return normalized.split(' ').length // Java: split(" ")
}
